import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const EXPORTS = [
  { key: "customers", label: "Customers" },
  { key: "leads", label: "Leads (All)" },
  { key: "loans", label: "Loan Files" },
  { key: "employees", label: "Employees" },
];

export default function ExcelIO() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doExport = async (entity) => {
    try {
      const res = await api.get(`/excel/export/${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `loanjunction_${entity}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Choose an Excel/CSV file"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/excel/import/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.imported} · Skipped ${data.skipped} · Duplicates ${data.duplicates_count}`);
      setFile(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Excel Import / Export</h1>
      <p className="text-sm text-[#52525B]">Backup and bulk-load data.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-display font-bold text-[#06152D] flex items-center gap-2"><Download size={16} className="text-[#0052FF]" /> Export</h3>
          <p className="text-sm text-[#52525B] mt-1">Download an xlsx snapshot of any entity.</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {EXPORTS.map((e) => (
              <button key={e.key} onClick={() => doExport(e.key)} className="border border-[#E4E4E7] hover:border-[#0052FF] hover:bg-[#EBF2FF] text-sm font-medium text-[#06152D] rounded-lg px-3 py-2 text-left inline-flex items-center gap-2" data-testid={`export-${e.key}`}>
                <FileSpreadsheet size={14} className="text-[#0052FF]" /> {e.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-display font-bold text-[#06152D] flex items-center gap-2"><Upload size={16} className="text-[#0052FF]" /> Bulk Lead Import</h3>
          <p className="text-sm text-[#52525B] mt-1">Required headers: <code className="text-xs bg-[#F4F4F5] px-1 rounded">full_name, phone</code>. Optional: <code className="text-xs bg-[#F4F4F5] px-1 rounded">loan_type, email, city, loan_amount</code>.</p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2 inline-flex items-start gap-1">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            All imported records → <b>Status: Enquiry</b>, <b>Source: Bulk Lead</b>. Duplicates (by mobile) are automatically detected & skipped.
          </p>
          <div className="mt-4 space-y-3">
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="excel-import-file" />
            <button onClick={doImport} disabled={busy} className="w-full bg-[#0052FF] hover:bg-[#003FCC] disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-full" data-testid="excel-import-submit">
              {busy ? "Importing…" : "Import Leads"}
            </button>
          </div>
          {result && (
            <div className="mt-4 border border-[#E4E4E7] rounded-lg p-4 space-y-2" data-testid="import-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono-num text-emerald-800" data-testid="import-imported-count">{result.imported}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Imported</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono-num text-amber-800" data-testid="import-skipped-count">{result.skipped}</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700">Skipped</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono-num text-red-800" data-testid="import-duplicates-count">{result.duplicates_count}</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-700">Duplicates</div>
                </div>
              </div>
              {result.duplicates && result.duplicates.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs font-semibold text-[#52525B] cursor-pointer">Show duplicate details ({result.duplicates.length})</summary>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-[#E4E4E7] rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F4F4F5]"><tr><th className="text-left px-2 py-1">Phone</th><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Reason</th></tr></thead>
                      <tbody>
                        {result.duplicates.map((d, i) => (
                          <tr key={i} className="border-t border-[#E4E4E7]"><td className="px-2 py-1 font-mono-num">{d.phone}</td><td className="px-2 py-1">{d.name || "—"}</td><td className="px-2 py-1"><Badge variant="secondary" className="text-[10px]">{d.reason}</Badge> {d.existing_lead_id && <span className="ml-1 font-mono-num text-[10px] text-[#52525B]">{d.existing_lead_id}</span>}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
